import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Pencil, UserCheck, UserX, Search } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Pagination } from '@/components/ui/Pagination';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Modal } from '@/components/ui/Modal';
import { createUserInput } from '@portal-leo/shared';
import type { z } from 'zod';

type CreateUserInput = z.infer<typeof createUserInput>;

const editUserSchema = createUserInput.omit({ senha: true });
type EditUserData = z.infer<typeof editUserSchema>;

const perfilLabels: Record<string, string> = {
  ADMIN: 'Administrador',
  FINANCEIRO: 'Financeiro',
  CAIXA: 'Caixa',
  COORDENADOR_CAMPANHA: 'Coord. Campanha',
  COORDENADOR_EVENTO: 'Coord. Evento',
  VISUALIZADOR: 'Visualizador',
};

const perfilColors: Record<string, string> = {
  ADMIN: 'bg-purple-100 text-purple-800',
  FINANCEIRO: 'bg-blue-100 text-blue-800',
  CAIXA: 'bg-green-100 text-green-800',
  COORDENADOR_CAMPANHA: 'bg-orange-100 text-orange-800',
  COORDENADOR_EVENTO: 'bg-teal-100 text-teal-800',
  VISUALIZADOR: 'bg-gray-100 text-gray-800',
};

export function UsuariosPage() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ADMIN');

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [selectedCargoIds, setSelectedCargoIds] = useState<string[]>([]);

  const utils = trpc.useUtils();

  const { data, isLoading } = trpc.users.list.useQuery({
    page,
    limit: 10,
    search: search || undefined,
  });

  const { data: cargosList } = trpc.cargos.list.useQuery({ ativo: true });

  const createMutation = trpc.users.create.useMutation({
    onSuccess: () => {
      utils.users.list.invalidate();
      handleCloseModal();
    },
  });

  const updateMutation = trpc.users.update.useMutation({
    onSuccess: () => {
      utils.users.list.invalidate();
      handleCloseModal();
    },
  });

  const createForm = useForm<CreateUserInput>({
    resolver: zodResolver(createUserInput),
    defaultValues: { cargoIds: [] },
  });

  const editForm = useForm<EditUserData>({
    resolver: zodResolver(editUserSchema),
    defaultValues: { cargoIds: [] },
  });

  const isEditing = !!editingUserId;

  const toggleCargo = (cargoId: string) => {
    setSelectedCargoIds((prev) =>
      prev.includes(cargoId) ? prev.filter((id) => id !== cargoId) : [...prev, cargoId]
    );
  };

  const onCreateSubmit = (data: CreateUserInput) => {
    createMutation.mutate({ ...data, cargoIds: selectedCargoIds });
  };

  const onEditSubmit = (data: EditUserData) => {
    if (editingUserId) {
      updateMutation.mutate({ id: editingUserId, ...data, cargoIds: selectedCargoIds });
    }
  };

  const handleEdit = (user: { id: string; nome: string; email: string; ativo: boolean; cargos: { id: string; nome: string }[] }) => {
    setEditingUserId(user.id);
    const cargoIds = user.cargos.map((c) => c.id);
    setSelectedCargoIds(cargoIds);
    editForm.reset({
      nome: user.nome,
      email: user.email,
      cargoIds,
    });
    setIsModalOpen(true);
  };

  const handleToggleStatus = (user: { id: string; ativo: boolean }) => {
    updateMutation.mutate({ id: user.id, ativo: !user.ativo });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUserId(null);
    setSelectedCargoIds([]);
    createForm.reset();
    editForm.reset();
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Compute derived permissoes from selected cargos
  const derivedPermissoes = (() => {
    if (!cargosList) return [];
    const perms = new Set<string>();
    for (const cargoId of selectedCargoIds) {
      const cargo = cargosList.find((c) => c.id === cargoId);
      if (cargo) {
        for (const p of cargo.permissoes) {
          perms.add(p);
        }
      }
    }
    return [...perms];
  })();

  const CargoCheckboxes = () => (
    <div>
      <label className="mb-2 block text-sm font-medium text-gray-700">Cargos</label>
      <div className="space-y-2 rounded-lg border border-gray-200 p-3">
        {cargosList?.map((cargo) => (
          <label key={cargo.id} className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedCargoIds.includes(cargo.id)}
              onChange={() => toggleCargo(cargo.id)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">{cargo.nome}</span>
            <span className="ml-auto flex gap-1">
              {cargo.permissoes.map((p) => (
                <span key={p} className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${perfilColors[p]}`}>
                  {perfilLabels[p]}
                </span>
              ))}
            </span>
          </label>
        ))}
      </div>
      {derivedPermissoes.length > 0 && (
        <div className="mt-2">
          <p className="text-xs text-gray-500 mb-1">Permissoes derivadas:</p>
          <div className="flex flex-wrap gap-1">
            {derivedPermissoes.map((p) => (
              <span key={p} className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${perfilColors[p]}`}>
                {perfilLabels[p]}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Usuarios</h1>
        <Button onClick={() => setIsModalOpen(true)} disabled={!canEdit}>
          <Plus className="mr-2 h-4 w-4" />
          Novo Usuario
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome ou email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Cargos</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Acoes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.items.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.nome}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {user.cargos.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {user.cargos.map((c) => (
                              <span key={c.id} className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                                {c.nome}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                            user.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {user.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(user)} disabled={!canEdit} className={!canEdit ? 'opacity-50 cursor-not-allowed' : ''}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleStatus(user)}
                            disabled={!canEdit}
                            className={!canEdit ? 'opacity-50 cursor-not-allowed' : ''}
                            title={user.ativo ? 'Desativar' : 'Ativar'}
                          >
                            {user.ativo ? (
                              <UserX className="h-4 w-4 text-red-600" />
                            ) : (
                              <UserCheck className="h-4 w-4 text-green-600" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {data && <Pagination meta={data.meta} onPageChange={setPage} itemLabel="usuários" />}
            </>
          )}
        </CardContent>
      </Card>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={isEditing ? 'Editar Usuario' : 'Novo Usuario'}>
        {isEditing ? (
          <form onSubmit={editForm.handleSubmit(onEditSubmit)} className="space-y-4">
            <Input label="Nome" {...editForm.register('nome')} error={editForm.formState.errors.nome?.message} />
            <Input label="Email" type="email" {...editForm.register('email')} error={editForm.formState.errors.email?.message} />
            <CargoCheckboxes />
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button type="submit" isLoading={isPending}>
                Salvar
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={createForm.handleSubmit(onCreateSubmit)} className="space-y-4">
            <Input label="Nome" {...createForm.register('nome')} error={createForm.formState.errors.nome?.message} />
            <Input label="Email" type="email" {...createForm.register('email')} error={createForm.formState.errors.email?.message} />
            <Input label="Senha" type="password" {...createForm.register('senha')} error={createForm.formState.errors.senha?.message} />
            <CargoCheckboxes />
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button type="submit" isLoading={isPending}>
                Criar
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

export default UsuariosPage;
